import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

/**
 * Zeilen einer Liste der Verwaltung samt Lade- und Fehlerzustand.
 *
 * `useAdminRows` (geteilt mit REQ-29) kennt nur die Zeilen. `MasterDetail` und die Unterlisten
 * brauchen nach REQ-28 §1 zusätzlich „Wird geladen …", den Fehlerzustand mit „Erneut versuchen"
 * und — für das sofortige Speichern der Rechte — einen Zugriff auf die Zeilen, der eine
 * Änderung optimistisch vorwegnimmt und sie bei Misserfolg zurücknimmt.
 */
export interface AdminList<T> {
  rows: T[];
  /** Für optimistische Änderungen an einer Zeile, ohne neu zu laden. */
  setRows: Dispatch<SetStateAction<T[]>>;
  loading: boolean;
  /** `null`, solange kein Fehler vorliegt. */
  error: unknown;
  reload: () => Promise<void>;
}

/**
 * Lädt beim Einhängen und immer dann neu, wenn sich `load` ändert — `load` muss deshalb über
 * Renderzyklen stabil sein (Modulkonstante oder `useCallback`).
 *
 * Die Dienste in `src/api/services/` fangen Fehler ab und liefern eine leere Liste
 * (REQ-00 §8); der Fehlerzustand greift daher erst, wenn ein Aufruf tatsächlich abbricht.
 * Die Verdrahtung steht trotzdem, weil REQ-28 §1 sie verlangt.
 */
export function useAdminList<T>(load: () => Promise<T[]>): AdminList<T> {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  // Verwirft Antworten überholter Ladevorgänge — ein Wechsel des Datensatzes während des
  // Ladens zeigte sonst die Zeilen des vorigen.
  const generation = useRef(0);

  const reload = useCallback(async () => {
    generation.current += 1;
    const current = generation.current;
    setLoading(true);
    setError(null);
    try {
      const loaded = await load();
      if (generation.current !== current) return;
      setRows(loaded);
    } catch (failure) {
      if (generation.current !== current) return;
      setRows([]);
      setError(failure ?? new Error('Der Grund ist nicht bekannt.'));
    } finally {
      if (generation.current === current) setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { rows, setRows, loading, error, reload };
}
